/**
 * imageSelection extension (editor-image-tools) — NodeSelection(image) 진입/이탈을 감지하여
 * 어댑터(`NgwEditorImage.setSelectedImage`)에 통지한다. ImageToolbar(React portal)가 이 상태를
 * 구독해 Floating 툴바를 띄운다.
 *
 * 설계(옵션 B, design §6):
 *   - 내장 image nodeView / ResizeTracker / CSS transform 을 **override 하지 않는다**. 선택 감지만
 *     별도 PM plugin 으로 추가 → SE2(restoreImageDimensions 키 변경)·SE4(nodeView 더블디스패치) 회피.
 *   - PM `Plugin` 클래스는 @ngweditor/react 가 runtime export 하지 않으므로, mailRegions 와 동일하게
 *     `view.state.plugins[0].constructor` 로 BundledPlugin 을 추출하여 plugin view 를 가진 인스턴스를
 *     생성하고 `reconfigure` 로 주입한다(mailRegions 의 reconfigure 결과 plugins 를 보존).
 *   - 선택 노드의 DOM 은 ProseMirror 가 부여하는 `.ProseMirror-selectednode` 로 조회(위치 계산용).
 */

import type { NgwExtension, EditorView, Plugin } from '@ngweditor/react'
import type { NgwEditorAdapter } from '../NgwEditorAdapter'

const IMAGE_NODE_NAME = 'image'
const SELECTED_NODE_CLASS = 'ProseMirror-selectednode'

/** BundledPlugin duck-typing — plugin view 로 selection 변화 구독. */
interface ImageSelectionPluginSpec {
  view?: (view: EditorView) => {
    update: (view: EditorView) => void
    destroy?: () => void
  }
}

/** NodeSelection 구조 접근(런타임 NodeSelection 클래스 미export 회피). */
interface MaybeNodeSelection {
  from: number
  node?: { type: { name: string }; attrs: Record<string, unknown> }
}

export function createImageSelectionExtension(adapter: NgwEditorAdapter): NgwExtension {
  return {
    name: 'image-selection',
    setup(ctx) {
      const view = ctx.editor.view
      const firstPlugin = view.state.plugins[0]
      const BundledPlugin = firstPlugin?.constructor as
        | (new (spec: ImageSelectionPluginSpec) => Plugin)
        | undefined
      if (BundledPlugin == null) {
        return
      }

      const selectionPlugin = new BundledPlugin({
        view: () => ({
          update: (pluginView) => syncSelection(adapter, pluginView),
          destroy: () => adapter.image.setSelectedImage(null),
        }),
      })

      view.updateState(
        view.state.reconfigure({
          plugins: [...view.state.plugins, selectionPlugin],
        }),
      )

      // 초기 1회 동기화 (이미 이미지가 선택된 상태로 마운트된 경우 대비).
      syncSelection(adapter, view)
    },
  }
}

function syncSelection(adapter: NgwEditorAdapter, view: EditorView): void {
  const selection = view.state.selection as unknown as MaybeNodeSelection
  const node = selection.node
  if (node != null && node.type.name === IMAGE_NODE_NAME) {
    const src = typeof node.attrs.src === 'string' ? node.attrs.src : ''
    const dom = view.dom.querySelector(`.${SELECTED_NODE_CLASS}`)
    if (src.length > 0 && dom instanceof HTMLElement) {
      adapter.image.setSelectedImage({ pos: selection.from, src, dom })
      return
    }
  }
  adapter.image.setSelectedImage(null)
}
